import { useMemo } from 'react';
import { resolveCountryIso3 } from '@upbeat/shared';
import { useRanking, useMultiCountryProfiles } from '../api/hooks';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { Panel } from '../components/Panel';
import {
  AfricaMap,
  isAfricanCountry,
  type CountryData,
} from '../charts/AfricaMap';
import { DEFAULT_COMPARE_COLORS } from '../charts/palette';
import { CountryCompare } from '../components/CountryCompare';
import { RadarChart } from '../charts/RadarChart';
import { AFR_MAX_YEAR, isEmptySelection } from '../state/filters';
import { PageShell, usePage } from './PageShell';
import { t } from '../i18n';

export function MapPage() {
  const { state, query, selectedCountries = [], setSelectedCountries, meta } = usePage();

  const allIndicators = useMemo(() => {
    if (!meta?.indicators) return undefined;
    return meta.indicators.flatMap((c) =>
      c.indicators.map((i) => ({
        indicator: i.indicator,
        component: c.component,
        dimension: c.dimension,
        units: i.units,
        inverted: i.inverted,
        description: i.description,
        formula: i.formula,
        unit: i.unit,
      }))
    );
  }, [meta]);

  const empty = state ? isEmptySelection(state) : false;
  const mapYear = state?.mapYear ?? AFR_MAX_YEAR;
  const mapQuery = useMemo(
    () => (query ? { ...query, from: mapYear, to: mapYear } : null),
    [query, mapYear]
  );
  const ranking = useRanking(empty ? null : mapQuery);
  const d = ranking.data;

  // Aggregate ranking rows into African country-level statistics
  const { countriesData } = useMemo(() => {
    const map = new Map<string, CountryData>();
    if (!d?.rows) return { countriesData: map };

    const grouped = new Map<string, typeof d.rows>();
    for (const r of d.rows) {
      if (!isAfricanCountry(r.country)) continue;
      if (!grouped.has(r.country)) {
        grouped.set(r.country, []);
      }
      grouped.get(r.country)!.push(r);
    }

    for (const [cName, uRows] of grouped.entries()) {
      const vals = uRows.map((r) => r.value).sort((a, b) => a - b);
      const mid = Math.floor(vals.length / 2);
      const median = vals.length % 2 === 0 ? (vals[mid - 1]! + vals[mid]!) / 2 : (vals[mid] ?? 0);

      map.set(cName, {
        country: cName,
        iso3c: resolveCountryIso3(cName) ?? undefined,
        region: uRows[0]?.region,
        median,
        n: uRows.length,
        min: vals[0] ?? median,
        max: vals[vals.length - 1] ?? median,
        utilities: uRows.map((r) => ({
          id: r.utility_id,
          name: r.utility,
          ownership: r.ownership,
          type: r.type,
          value: r.value,
          rank: r.rank,
        })),
      });
    }

    return { countriesData: map };
  }, [d?.rows]);

  // Continental African median for active indicator
  const africaMedian = useMemo(() => {
    if (!d?.rows || d.rows.length === 0) return null;
    const vals = d.rows.map((r) => r.value).sort((a, b) => a - b);
    const mid = Math.floor(vals.length / 2);
    return vals.length % 2 === 0 ? (vals[mid - 1]! + vals[mid]!) / 2 : (vals[mid] ?? null);
  }, [d?.rows]);

  // Color assignments for comparison (up to 4 countries)
  const countryColors = useMemo(() => {
    const map: Record<string, string> = {};
    selectedCountries.forEach((c, i) => {
      map[c] = DEFAULT_COMPARE_COLORS[i % DEFAULT_COMPARE_COLORS.length]!;
    });
    return map;
  }, [selectedCountries]);

  // Toggle country selection (limit 4)
  const toggleCountry = (countryName: string) => {
    if (!setSelectedCountries) return;
    setSelectedCountries((prev: string[]) => {
      if (prev.includes(countryName)) {
        return prev.filter((c) => c !== countryName);
      }
      if (prev.length >= 4) {
        return [...prev.slice(1), countryName];
      }
      return [...prev, countryName];
    });
  };

  // Multi-country profiles for radar chart
  const profileQueries = useMultiCountryProfiles(selectedCountries, mapYear);
  const radarSeries = useMemo(() => {
    return selectedCountries
      .map((c, i) => {
        const q = profileQueries[i];
        return {
          country: c,
          iso3c: q?.data?.iso3c || resolveCountryIso3(c),
          color: countryColors[c] || DEFAULT_COMPARE_COLORS[i % DEFAULT_COMPARE_COLORS.length]!,
          indicators: q?.data?.indicators || [],
        };
      })
      .filter((s) => s.indicators.length > 0);
  }, [selectedCountries, profileQueries, countryColors]);

  const refNote = d?.ref_year ? t.refYear(d.ref_year) : undefined;
  const headerNote = (
    <div className="flex items-center gap-2">
      {state?.indicator && <span>{state.indicator}</span>}
      {refNote && <span className="text-dim">· {refNote}</span>}
    </div>
  );

  return (
    <PageShell title={t.mapTitle} note={headerNote}>
      {/* Top Map and Side-by-Side Comparison Section with equal card heights */}
      <div className="cols-6-6 items-stretch">
        {/* Left Column: The Africa Interactive Map */}
        <section className="section flex flex-col min-h-0">
          <div className="panel flex-1 flex flex-col min-h-0 p-4">
            {ranking.isError ? (
              <ErrorState error={ranking.error} />
            ) : ranking.isPending && !empty ? (
              <div className="skeleton flex-1 min-h-[480px]" />
            ) : !d?.rows.length || empty ? (
              <EmptyState />
            ) : (
              <AfricaMap
                countriesData={countriesData}
                units={d.units}
                inverted={d.inverted}
                selectedCountry={selectedCountries[0] ?? null}
                onSelectCountry={(c) => { if (c) toggleCountry(c); }}
                multiSelect={true}
                selectedCountries={selectedCountries}
                onToggleCountry={toggleCountry}
                countryColors={countryColors}
              />
            )}
          </div>
        </section>

        {/* Right Column: Selected Countries Comparison & Utility Breakdown */}
        <section className="section flex flex-col min-h-0">
          <CountryCompare
            selectedCountries={selectedCountries}
            countriesData={countriesData}
            units={d?.units ?? null}
            unit={d?.unit ?? null}
            description={d?.description ?? null}
            formula={d?.formula ?? null}
            inverted={d?.inverted}
            indicator={d?.indicator ?? (state?.indicator || '')}
            africaMedian={africaMedian}
            countryColors={countryColors}
            onRemoveCountry={(c) => setSelectedCountries?.((prev: string[]) => prev.filter((x: string) => x !== c))}
            onClearAll={() => setSelectedCountries?.([])}
          />
        </section>
      </div>

      {/* Bottom Section: Unified Multi-Country Radar Chart */}
      {selectedCountries.length > 0 && radarSeries.length > 0 && (
        <section className="section pt-2 pb-6">
          <div className="shead mb-1.5">
            <h3 className="font-serif text-lg font-bold text-txt">
              {t.radarDiagTitle}
            </h3>
            <div className="note text-xs text-dim">
              {t.radarDiagSubtitle}
            </div>
          </div>

          <div className="p-2 sm:p-3 rounded-lg border border-hair bg-glass">
            <RadarChart
              series={radarSeries}
              selectedIndicator={state?.indicator || d?.indicator}
              size={520}
              allIndicators={allIndicators}
            />
          </div>
        </section>
      )}
    </PageShell>
  );
}
